import React, { useEffect } from 'react';
import s from './Drawer.module.css';

// The right-side drawer.
//
// Every long form in MMS opens this way rather than as a centred modal: the
// student wizard, the event form, attendance, the invoice wizard. One
// component keeps escape to close, a sticky footer and the expand control the
// same everywhere.
//
// `steps` renders the numbered progress header the multi-step wizards use.
// `onBack` renders the back arrow the stacked drawers use — MMS pushes a second
// panel over the first (Add Revenue → New Category) rather than opening a nested
// modal, and the arrow is what returns you to the form with its state intact.
export default function Drawer({
  open,
  title,
  onClose,
  onBack = null,
  children,
  footer,
  steps = null,
  currentStep = 0,
  width = 700,
  expanded = false,
  onToggleExpand = null,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>

      <div className={s.scrim} onClick={onClose} />
      <aside
        className={s.drawer}
        style={{ width: expanded ? '100vw' : `min(${width}px, 100vw)` }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={s.head}>
          {onBack ? (
            <button className={s.iconButton} onClick={onBack} aria-label="Back">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M9.5 3.5L5 8l4.5 4.5M5 8h7" stroke="currentColor"
                      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : null}
          <div className={s.title}>{title}</div>
          {onToggleExpand ? (
            <button className={s.iconButton} onClick={onToggleExpand} aria-label="Toggle full width">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path d="M2 5.5V2h3.5M13 9.5V13H9.5M13 5.5V2H9.5M2 9.5V13h3.5"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}
          <button className={s.iconButton} onClick={onClose} aria-label="Close">
            <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true">
              <path d="M3.5 3.5l8 8M11.5 3.5l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {steps ? (
          <div className={s.steps}>
            {steps.map((label, i) => (
              <div className={s.step} key={label}>
                <span className={i < currentStep ? s.stepNumDone : i === currentStep ? s.stepNumCurrent : s.stepNum}>
                  {i < currentStep ? '✓' : i + 1}
                </span>
                <span className={i <= currentStep ? s.stepLabelActive : s.stepLabel}>{label}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className={s.body}>{children}</div>
        {footer ? <div className={s.foot}>{footer}</div> : null}
      </aside>
    </>
  );
}

// The gap that pushes a footer's primary action to the right. A component
// rather than a raw class name, which module scoping would rename.
export function FooterSpacer() {
  return <span className={s.footSpacer} />;
}
